import { useEffect, useRef, useState } from 'react'
import { focusManagement } from '../utils/accessibility'

/**
 * Hook for managing accessibility features
 */
export const useAccessibility = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [announcements, setAnnouncements] = useState<string[]>([])
  const announcementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  /**
   * Announce message to screen readers
   */
  const announce = (message: string, _priority: 'polite' | 'assertive' = 'polite') => {
    setAnnouncements(prev => [...prev, message])
    
    // Clear announcement after delay
    setTimeout(() => {
      setAnnouncements(prev => prev.slice(1))
    }, 1000)
  }

  /**
   * Get animation props respecting user preferences
   */
  const getAnimationProps = (normalProps: any) => {
    return prefersReducedMotion ? { ...normalProps, duration: 0 } : normalProps
  }

  return {
    prefersReducedMotion,
    announce,
    getAnimationProps,
    announcements,
    announcementRef
  }
}

/**
 * Hook for managing keyboard navigation in lists
 */
export const useKeyboardNavigation = (itemCount: number) => {
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const itemRefs = useRef<(HTMLElement | null)[]>([])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setFocusedIndex(prev => (prev + 1) % itemCount)
        break
      case 'ArrowUp':
        event.preventDefault()
        setFocusedIndex(prev => prev === 0 ? itemCount - 1 : prev - 1)
        break
      case 'Home':
        event.preventDefault()
        setFocusedIndex(0)
        break
      case 'End':
        event.preventDefault()
        setFocusedIndex(itemCount - 1)
        break
      case 'Escape':
        setFocusedIndex(-1)
        break
    }
  }

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus()
    }
  }, [focusedIndex])

  const setItemRef = (index: number) => (ref: HTMLElement | null) => {
    itemRefs.current[index] = ref
  }

  return {
    focusedIndex,
    handleKeyDown,
    setItemRef,
    setFocusedIndex
  }
}

/**
 * Hook for managing focus trap in modals/menus
 */
export const useFocusTrap = (isActive: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isActive || !containerRef.current) return

    // Store previously focused element
    previousFocusRef.current = document.activeElement as HTMLElement

    // Focus first element in container
    focusManagement.focusFirst(containerRef.current)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (containerRef.current) {
        focusManagement.trapFocus(containerRef.current, event)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      
      // Restore focus to previously focused element
      if (previousFocusRef.current) {
        previousFocusRef.current.focus()
      }
    }
  }, [isActive])

  return containerRef
}

/**
 * Hook for managing skip links
 */
export const useSkipLinks = () => {
  const skipToContent = () => {
    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      mainContent.focus()
      mainContent.scrollIntoView()
    }
  }

  const skipToNavigation = () => {
    const navigation = document.getElementById('main-navigation')
    if (navigation) {
      navigation.focus()
      navigation.scrollIntoView()
    }
  }

  return {
    skipToContent,
    skipToNavigation
  }
}